import React, { useContext, useState } from "react"
import Header from "../../layouts/Header"
import { styled, createTheme, ThemeProvider } from "@mui/material/styles"
import {
  Box,
  Button,
  Grid,
  TextField,
  MenuItem,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Autocomplete,
} from "@mui/material"
import BackIcon from "../../components/BackIcon"
import { useNavigate } from "react-router-dom"
import CustomConditionalActionsTableServer from "../../components/CustomConditionalActionsTableServerSide"
import normalFormatDate from "../utils/date/DDMMYYYFormatDate"
import { GlobalContext } from "../../contexts/GlobalContext"
import { useQuery } from "@tanstack/react-query"
import { api } from "../../api"
import CustomBackdrop from "../../components/CustomBackdrop"
import CustomDatePicker from "../../components/CustomDatePicker"
import dayjs from "dayjs"

// Importación para generar íconos dinámicos según estándar
import getIconComponent from "../utils/getIconComponent"

import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import SearchIcon from "@mui/icons-material/Search"
import CheckCircleIcon from "@mui/icons-material/CheckCircle"

const StyledRoot = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  marginTop: theme.spacing(8),
  flexGrow: 1,
  padding: "0 16px",
  height: "100vh",
}))

const theme = createTheme({
  palette: {
    primary: { main: "#196C87" },
    secondary: { main: "#2E7D32" },
  },
})

const ConsultasCobrosIndex = () => {
  const navigate = useNavigate()
  const { selectedMenuInfo } = useContext(GlobalContext)

  // Controladores de estado visual
  const [expanded, setExpanded] = useState(true)
  const [refreshTableKey, setRefreshTableKey] = useState(0)

  // Estado centralizado de filtros principales
  const [filtros, setFiltros] = useState({
    loccodigo: "",
    locdescri: "",
    reccodigo: "",
    recnombre: "",
    seqcodigo: "",
    seqdescri: "",
    compIni: "",
    compFin: "",
    fecDes: null,
    fecHas: null,
    clicodigo: "",
    clinombre: "",
    montoSigno: "=",
    montoValor: "",
    facnumfac: "",
    recap: "",
    voucher: "",
  })

  // Estados para el Modal de Búsqueda de Clientes
  const [clientModalOpen, setClientModalOpen] = useState(false)
  const [clientSearch, setClientSearch] = useState({ identificacion: "", nombre: "" })
  const [clientSearchKey, setClientSearchKey] = useState(0)

  // Consulta de Catálogos (Se dispara al abrir la pantalla)
  const { data: rawCatalogos, isLoading: isLoadingCatalogos } = useQuery({
    queryKey: ["getCatalogosFiltrosCobro"],
    queryFn: async () => {
      try {
        const response = await api.post("/ConsultasCobrosAplA/getCatalogosFiltrosCobro", {})
        return response?.data?.data || response?.data || {}
      } catch (error) {
        console.error("Error al cargar catálogos:", error)
        return {}
      }
    },
    refetchOnWindowFocus: false,
  })

  // Extracción Defensiva de Arreglos para Combos
  const baseCatalogos = rawCatalogos || {}

  const rawLocalidades = baseCatalogos?.localidades || baseCatalogos?.data?.localidades || []
  const listaLocalidades = Array.isArray(rawLocalidades) ? rawLocalidades : []

  const rawRecaudadores = baseCatalogos?.recaudadores || baseCatalogos?.data?.recaudadores || []
  const listaRecaudadores = Array.isArray(rawRecaudadores) ? rawRecaudadores : []

  const rawSecuencias = baseCatalogos?.secuencias || baseCatalogos?.data?.secuencias || []
  const listaSecuencias = Array.isArray(rawSecuencias) ? rawSecuencias : []

  // Consulta de Clientes para el Modal (Se dispara al presionar Buscar en el modal)
  const { data: clientesBusqueda = [], isFetching: isFetchingClientes } = useQuery({
    queryKey: ["buscarClientesFiltro", clientSearchKey],
    queryFn: async () => {
      if (clientSearchKey === 0) return []
      try {
        const response = await api.post("/ConsultasCobrosAplA/buscarClientesFiltro", clientSearch)
        // Extracción defensiva estricta para evitar .map is not a function
        const rawData = response?.data?.data || response?.data || response || []
        return Array.isArray(rawData) ? rawData : Array.isArray(rawData?.data) ? rawData.data : []
      } catch (error) {
        console.error("Error al buscar clientes:", error)
        return []
      }
    },
    enabled: clientSearchKey > 0,
    refetchOnWindowFocus: false,
  })

  const handleInputChange = (field, value) => {
    setFiltros((prev) => ({ ...prev, [field]: value }))
  }

  const handleFiltrar = () => {
    setRefreshTableKey((prev) => prev + 1)
    setExpanded(false)
  }

  const handleSelectCliente = (cliente) => {
    handleInputChange("clicodigo", cliente.clicodigo)
    handleInputChange("clinombre", cliente.clinombre)
    setClientModalOpen(false)
  }

  return (
    <ThemeProvider theme={theme}>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <BackIcon />
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            margin: "0 30px 30px 30px",
            fontSize: "25px",
          }}
        >
          <b>Cobros aplicados a...</b>
        </div>

        <CustomBackdrop isLoading={isLoadingCatalogos} />

        <Box className={StyledRoot}>
          {/* ACORDEÓN DE FILTROS */}
          <Accordion
            expanded={expanded}
            onChange={() => setExpanded(!expanded)}
            sx={{ mb: 3, borderRadius: 2, boxShadow: 3, "&:before": { display: "none" } }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: "#f5f7fa", borderRadius: 2 }}>
              <Typography variant="subtitle1" fontWeight="bold" color="primary">
                Parámetros de Búsqueda
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                {/* Fila 1: Catálogos */}
                <Grid item xs={12} sm={4}>
                  <Autocomplete
                    options={listaLocalidades}
                    getOptionLabel={(option) => option.label || ""}
                    value={listaLocalidades.find((o) => o.id === filtros.loccodigo) || null}
                    isOptionEqualToValue={(option, value) => option.id === value?.id}
                    onChange={(event, newValue) => {
                      handleInputChange("loccodigo", newValue ? newValue.id : "")
                      handleInputChange("locdescri", newValue ? newValue.label : "")
                    }}
                    renderInput={(params) => (
                      <TextField {...params} label="Localidad" size="small" InputLabelProps={{ shrink: true }} />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Autocomplete
                    options={listaRecaudadores}
                    getOptionLabel={(option) => option.label || ""}
                    value={listaRecaudadores.find((o) => o.id === filtros.reccodigo) || null}
                    isOptionEqualToValue={(option, value) => option.id === value?.id}
                    onChange={(event, newValue) => {
                      handleInputChange("reccodigo", newValue ? newValue.id : "")
                      handleInputChange("recnombre", newValue ? newValue.label : "")
                    }}
                    renderInput={(params) => (
                      <TextField {...params} label="Recaudador" size="small" InputLabelProps={{ shrink: true }} />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Autocomplete
                    options={listaSecuencias}
                    getOptionLabel={(option) => option.label || ""}
                    value={listaSecuencias.find((o) => o.id === filtros.seqcodigo) || null}
                    isOptionEqualToValue={(option, value) => option.id === value?.id}
                    onChange={(event, newValue) => {
                      handleInputChange("seqcodigo", newValue ? newValue.id : "")
                      handleInputChange("seqdescri", newValue ? newValue.label : "")
                    }}
                    renderInput={(params) => (
                      <TextField {...params} label="Secuencia" size="small" InputLabelProps={{ shrink: true }} />
                    )}
                  />
                </Grid>

                {/* Fila 2: Rangos y Fechas con CustomDatePicker (Formato DD/MM/YYYY UI) */}
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Comprobante Inicial"
                    size="small"
                    value={filtros.compIni}
                    onChange={(e) => handleInputChange("compIni", e.target.value.toUpperCase())}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Comprobante Final"
                    size="small"
                    value={filtros.compFin}
                    onChange={(e) => handleInputChange("compFin", e.target.value.toUpperCase())}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3} sx={{ mt: { xs: 0, sm: -1 } }}>
                  <CustomDatePicker
                    label="Fecha Emisión Desde"
                    value={filtros.fecDes}
                    setValue={(v) => handleInputChange("fecDes", v)}
                    isOptional={true}
                    format="DD/MM/YYYY"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3} sx={{ mt: { xs: 0, sm: -1 } }}>
                  <CustomDatePicker
                    label="Fecha Emisión Hasta"
                    value={filtros.fecHas}
                    setValue={(v) => handleInputChange("fecHas", v)}
                    isOptional={true}
                    format="DD/MM/YYYY"
                  />
                </Grid>

                {/* Fila 3: Cliente y Montos */}
                <Grid item xs={12} sm={4} md={2}>
                  <TextField
                    fullWidth
                    label="Cód. Cliente"
                    size="small"
                    value={filtros.clicodigo}
                    onChange={(e) => handleInputChange("clicodigo", e.target.value.toUpperCase())}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => setClientModalOpen(true)} size="small" color="primary">
                            <SearchIcon />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={8} md={4}>
                  <TextField
                    fullWidth
                    label="Nombre Cliente (Coincidencia parcial)"
                    size="small"
                    value={filtros.clinombre}
                    onChange={(e) => handleInputChange("clinombre", e.target.value.toUpperCase())}
                  />
                </Grid>
                <Grid item xs={4} sm={4} md={2}>
                  <TextField
                    select
                    fullWidth
                    label="Operador"
                    size="small"
                    value={filtros.montoSigno}
                    onChange={(e) => handleInputChange("montoSigno", e.target.value)}
                  >
                    <MenuItem value="=">Igual (=)</MenuItem>
                    <MenuItem value=">">Mayor que (&gt;)</MenuItem>
                    <MenuItem value="<">Menor que (&lt;)</MenuItem>
                    <MenuItem value=">=">Mayor o igual (&gt;=)</MenuItem>
                    <MenuItem value="<=">Menor o igual (&lt;=)</MenuItem>
                    <MenuItem value="<>">Diferente (&lt;&gt;)</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={8} sm={8} md={4}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Monto"
                    size="small"
                    value={filtros.montoValor}
                    onChange={(e) => handleInputChange("montoValor", e.target.value)}
                  />
                </Grid>

                {/* Fila 4: Subconsultas / Aplicaciones */}
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Factura Aplicada"
                    size="small"
                    value={filtros.facnumfac}
                    onChange={(e) => handleInputChange("facnumfac", e.target.value.toUpperCase())}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Recap Aplicado"
                    size="small"
                    value={filtros.recap}
                    onChange={(e) => handleInputChange("recap", e.target.value.toUpperCase())}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Voucher Aplicado"
                    size="small"
                    value={filtros.voucher}
                    onChange={(e) => handleInputChange("voucher", e.target.value.toUpperCase())}
                  />
                </Grid>

                {/* Botón de Acción */}
                <Grid item xs={12} display="flex" justifyContent="flex-end" mt={2}>
                  <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    startIcon={<SearchIcon />}
                    onClick={handleFiltrar}
                    sx={{ px: 4, fontWeight: "bold" }}
                  >
                    Ejecutar Búsqueda
                  </Button>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          {/* TABLA DE RESULTADOS */}
          {refreshTableKey !== 0 && (
            <CustomConditionalActionsTableServer
              key={refreshTableKey}
              endpoint="/ConsultasCobrosAplA/getConsultaCobros"
              endpointJson={{
                externalFilters: {
                  ...filtros,
                  // Formateo seguro para la base de datos SQL
                  fecDes: filtros.fecDes ? dayjs(filtros.fecDes).format("YYYY-MM-DD") : "",
                  fecHas: filtros.fecHas ? dayjs(filtros.fecHas).format("YYYY-MM-DD") : "",
                },
              }}
              errorMsgFilterSearch="Error al cargar resultados de la búsqueda."
              queryKeyModal="ConsultasCobrosData"
              perPage={15}
              rowActionsWidthTable={100}
              rowActions={(row) => {
                const acciones = []

                // Aplicación de estándar de permisos para botón Detalle
                const detalleAction = selectedMenuInfo?.data?.barraAcciones?.find(
                  (action) => action?.acccaption === "DETALLE",
                )

                if (detalleAction) {
                  acciones.push({
                    label: "Ver Detalle",
                    key: "DETALLE",
                    icon: getIconComponent(detalleAction?.accnameicono, detalleAction?.acctipoico),
                    onClick: (row) =>
                      navigate("detalle", {
                        state: {
                          cxcnumcomp: row.original.cxcnumcomp,
                          ciacodigo: row.original.ciacodigo,
                        },
                      }),
                  })
                }

                return acciones
              }}
              columnsTable={[
                {
                  accessorKey: "ciacodigo",
                  header: "Cía",
                  size: 60,
                  Cell: ({ cell }) => <span>{cell.getValue()}</span>,
                },
                {
                  accessorKey: "cxcnumcomp",
                  header: "Comprobante",
                  size: 160,
                  Cell: ({ cell }) => <span style={{ fontWeight: "bold" }}>{cell.getValue()}</span>,
                },
                {
                  accessorKey: "cxcfecemi",
                  header: "Emisión",
                  size: 110,
                  Cell: ({ cell }) => <span>{normalFormatDate(cell.getValue())}</span>,
                },
                {
                  accessorKey: "clicodigo",
                  header: "Cód. Cliente",
                  size: 110,
                  Cell: ({ cell }) => <span>{cell.getValue()}</span>,
                },
                {
                  accessorKey: "clinombre",
                  header: "Cliente",
                  size: 250,
                  Cell: ({ cell }) => <span>{cell.getValue()}</span>,
                },
                {
                  accessorKey: "cxcmonto",
                  header: "Monto",
                  size: 100,
                  Cell: ({ cell }) => (
                    <span style={{ fontWeight: "bold", color: "#2E7D32" }}>
                      ${Number(cell.getValue() || 0).toFixed(2)}
                    </span>
                  ),
                },
                {
                  accessorKey: "ncompcodigo",
                  header: "Comp. Contable",
                  size: 150,
                  Cell: ({ cell }) => <span>{cell.getValue()}</span>,
                },
                {
                  accessorKey: "cxcdetalle",
                  header: "Descripción",
                  size: 250,
                  Cell: ({ cell }) => <span style={{ fontSize: "0.85rem" }}>{cell.getValue()}</span>,
                },
                {
                  accessorKey: "estado",
                  header: "Estado",
                  size: 120,
                  Cell: ({ cell }) => {
                    const status = cell.getValue()
                    return (
                      <span
                        style={{
                          color: status === "PROCESADO" ? "green" : status === "ANULADO" ? "red" : "black",
                          fontWeight: "bold",
                        }}
                      >
                        {status}
                      </span>
                    )
                  },
                },
                {
                  accessorKey: "origen",
                  header: "Origen",
                  size: 220,
                  Cell: ({ cell }) => <span style={{ fontSize: "0.85rem" }}>{cell.getValue()}</span>,
                },
                {
                  accessorKey: "cjadescri",
                  header: "Caja",
                  size: 150,
                  Cell: ({ cell }) => <span>{cell.getValue()}</span>,
                },
                {
                  accessorKey: "cxcusrisys",
                  header: "Usuario",
                  size: 100,
                  Cell: ({ cell }) => <span>{cell.getValue()}</span>,
                },
                {
                  accessorKey: "cxcfecisys",
                  header: "Fec. Sistema",
                  size: 110,
                  Cell: ({ cell }) => <span>{normalFormatDate(cell.getValue())}</span>,
                },
              ]}
            />
          )}
        </Box>

        {/* MODAL DE BÚSQUEDA DE CLIENTES */}
        <Dialog open={clientModalOpen} onClose={() => setClientModalOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle sx={{ bgcolor: "#f5f5f5", color: "primary.main", fontWeight: "bold" }}>
            Búsqueda Rápida de Clientes
          </DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={2} sx={{ mt: 1, mb: 3 }}>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Cédula / R.U.C."
                  value={clientSearch.identificacion}
                  onChange={(e) => setClientSearch({ ...clientSearch, identificacion: e.target.value })}
                  fullWidth
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Nombre del Cliente"
                  value={clientSearch.nombre}
                  onChange={(e) => setClientSearch({ ...clientSearch, nombre: e.target.value.toUpperCase() })}
                  fullWidth
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={() => setClientSearchKey((prev) => prev + 1)}
                  sx={{ height: "100%" }}
                  disabled={!clientSearch.identificacion && !clientSearch.nombre}
                >
                  Buscar
                </Button>
              </Grid>
            </Grid>

            {isFetchingClientes ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 300 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <b>Código</b>
                      </TableCell>
                      <TableCell>
                        <b>Identificación</b>
                      </TableCell>
                      <TableCell>
                        <b>Nombre</b>
                      </TableCell>
                      <TableCell align="center">
                        <b>Acción</b>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {clientesBusqueda.map((cli, idx) => (
                      <TableRow key={idx} hover>
                        <TableCell>{cli.clicodigo}</TableCell>
                        <TableCell>{cli.cliruc}</TableCell>
                        <TableCell>{cli.clinombre}</TableCell>
                        <TableCell align="center">
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<CheckCircleIcon />}
                            onClick={() => handleSelectCliente(cli)}
                          >
                            Seleccionar
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                    {clientesBusqueda.length === 0 && clientSearchKey > 0 && (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          No se encontraron clientes con los filtros ingresados.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setClientModalOpen(false)} color="inherit" sx={{ fontWeight: "bold" }}>
              Cerrar
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </ThemeProvider>
  )
}

export default ConsultasCobrosIndex
